import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { apiJson, apiForm } from '../../../components/apiClient';
import { Panel } from '../ui/Panel';
import { Badge, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { EmptyState } from '../ui/EmptyState';
import { EntityLink, UnlinkedField } from '../ui/EntityLink';
import { RowMenu } from '../ui/RowMenu';
import { VincularProjetoSelector } from './VincularProjetoSelector';
import { CobrancaWizardSheet } from './CobrancaWizardSheet';
import { RegistrarPagamentoDialog } from './RegistrarPagamentoDialog';

const apiFin = (path, opts) => apiJson(`/financeiro${path}`, opts);
const apiClientesFn = (path, opts) => apiJson(`/clientes${path}`, opts);

const fmtBRL = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && !isNaN(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const nomeCompetencia = (c) => {
  if (!c) return '—';
  const [a, m] = c.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');
};

// Linha "rótulo: valor" compacta do bloco de detalhes (§16 da correção de
// UX do Financeiro — menos "caixa dentro de caixa", uma lista densa em vez
// de vários Panels empilhados pra informação que não precisa de destaque
// próprio).
function LinhaDetalhe({ rotulo, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', padding: '5px 0', fontSize: 'var(--crm-text-sm)' }}>
      <span className="crm-form-hint" style={{ minWidth: '110px', flexShrink: 0 }}>{rotulo}</span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

// Detalhe de lançamento — estrutura compacta orientada a SALDO (§15/§16 da
// correção de UX/cálculo do Financeiro: o saldo em aberto é a informação
// de maior destaque operacional, nunca o valor bruto original; drawer
// reduzido a hero + 1 lista de detalhes + ações + histórico, em vez de
// vários Panels grandes empilhados). Componente compartilhado: usado em
// Financeiro > Receitas/Despesas e reaproveitado dentro de Cliente e
// Projeto.
export function LancamentoSheet({ item, onClose, onChanged, showToast, onNavegar, onAbrirAssinatura }) {
  const [detalhe, setDetalhe] = useState(null);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [pagamentoAberto, setPagamentoAberto] = useState(false);
  const [enviandoCobranca, setEnviandoCobranca] = useState(false);
  const [showVincularProjeto, setShowVincularProjeto] = useState(false);
  const vincularProjetoBtnRef = useRef(null);
  const [showCobranca, setShowCobranca] = useState(false);
  const [documentos, setDocumentos] = useState(null);
  const [showNovoDoc, setShowNovoDoc] = useState(false);
  const [docTitulo, setDocTitulo] = useState('');
  const [docArquivo, setDocArquivo] = useState(null);
  const [salvandoDoc, setSalvandoDoc] = useState(false);

  const isReceita = item._origem === 'receivable';
  const endpoint = isReceita ? `/receivables/${item.id}` : `/payables/${item.id}`;

  const carregar = useCallback(async () => {
    const data = await apiFin(endpoint);
    setDetalhe(data[isReceita ? 'receivable' : 'payable']);
  }, [endpoint, isReceita]);

  const carregarDocumentos = useCallback(async () => {
    const qs = new URLSearchParams({ entity_type: isReceita ? 'RECEIVABLE' : 'PAYABLE', entity_id: item.id });
    const r = await apiJson(`/documentos?${qs}`);
    if (r.status === 'ok') setDocumentos(r.lista);
  }, [isReceita, item.id]);

  useEffect(() => {
    carregar();
    carregarDocumentos();
    if (isReceita) apiClientesFn('').then((r) => setClientes(r.lista || []));
  }, [carregar, carregarDocumentos, isReceita]);

  useEffect(() => {
    if (!detalhe) return;
    setForm({
      descricao: detalhe.descricao, categoria: detalhe.categoria || '', competencia: detalhe.competencia,
      vencimento: detalhe.vencimento, forma_pagamento_prevista: detalhe.forma_pagamento_prevista || '',
      cliente_id: detalhe.cliente_id || '', fornecedor_nome: detalhe.fornecedor_nome || '',
      contato_nome: detalhe.contato_cobranca?.nome || '', contato_telefone: detalhe.contato_cobranca?.telefone || '', contato_email: detalhe.contato_cobranca?.email || '',
      valor: isReceita ? detalhe.valor : detalhe.valor_base,
      notas: detalhe.notas || '', observacoes: detalhe.observacoes || '',
    });
  }, [detalhe, isReceita]);

  const notificarMudanca = () => { carregar(); onChanged(); };

  const podeEditarValor = detalhe && detalhe.status !== 'PAGO' && detalhe.status !== 'CANCELADO';

  const salvarEdicao = async () => {
    const body = {
      descricao: form.descricao, categoria: form.categoria || null, competencia: form.competencia,
      vencimento: form.vencimento, forma_pagamento_prevista: form.forma_pagamento_prevista || null,
    };
    if (isReceita) {
      body.cliente_id = form.cliente_id || null;
      body.contato_cobranca = { nome: form.contato_nome, telefone: form.contato_telefone, email: form.contato_email };
      body.notas = form.notas || null;
      if (podeEditarValor) body.valor = form.valor;
    } else {
      body.fornecedor_nome = form.fornecedor_nome || null;
      body.observacoes = form.observacoes || null;
      if (podeEditarValor) body.valor_base = form.valor;
    }
    setSalvando(true);
    const r = await apiFin(endpoint, { method: 'PUT', body: JSON.stringify(body) });
    setSalvando(false);
    if (r.status === 'ok') { showToast(`${isReceita ? 'Receita' : 'Despesa'} atualizada!`, 'success'); setEditando(false); notificarMudanca(); }
    else showToast(r.mensagem || 'Erro ao salvar', 'error');
  };

  const vincularProjeto = async (projetoId) => {
    const r = await apiFin(endpoint, { method: 'PUT', body: JSON.stringify({ projeto_id: projetoId }) });
    if (r.status === 'ok') { showToast(projetoId ? 'Projeto vinculado!' : 'Vínculo com projeto removido.', 'success'); setShowVincularProjeto(false); notificarMudanca(); }
    else showToast(r.mensagem || 'Erro ao vincular projeto', 'error');
  };

  const estornar = async (pagamentoId) => {
    const { isConfirmed } = await Swal.fire({ title: 'Estornar este pagamento?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Estornar', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`${endpoint}/pagamentos/${pagamentoId}`, { method: 'DELETE', body: JSON.stringify({ motivo: 'Estorno manual' }) });
    if (r.status === 'ok') { showToast('Pagamento estornado.', 'success'); carregar(); onChanged(); }
    else showToast(r.mensagem || 'Erro ao estornar', 'error');
  };

  const cancelar = async () => {
    const { isConfirmed } = await Swal.fire({ title: 'Cancelar esta conta?', text: 'Pode ser desfeito depois — o registro fica visível com a opção "Reativar".', icon: 'warning', showCancelButton: true, confirmButtonText: 'Cancelar conta', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(endpoint, { method: 'DELETE' });
    if (r.status === 'ok') { showToast('Conta cancelada. Use "Reativar" no menu "..." para desfazer.', 'success'); notificarMudanca(); }
    else showToast(r.mensagem || 'Erro ao cancelar', 'error');
  };

  const reativar = async () => {
    const r = await apiFin(`${endpoint}/reativar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Cancelamento desfeito!', 'success'); notificarMudanca(); }
    else showToast(r.mensagem || 'Erro ao reativar', 'error');
  };

  const arquivar = async () => {
    const { isConfirmed } = await Swal.fire({ title: 'Arquivar esta conta?', text: 'Ela some da lista normal, mas nada é apagado.', icon: 'question', showCancelButton: true, confirmButtonText: 'Arquivar', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`${endpoint}/arquivar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Conta arquivada.', 'success'); notificarMudanca(); onClose(); }
    else showToast(r.mensagem || 'Erro ao arquivar', 'error');
  };

  const duplicar = async () => {
    const r = await apiFin(`${endpoint}/duplicar`, { method: 'POST' });
    if (r.status === 'ok') { showToast(`${isReceita ? 'Receita' : 'Despesa'} duplicada — vencimento hoje, revise antes de cobrar/pagar.`, 'success'); onChanged(); }
    else showToast(r.mensagem || 'Erro ao duplicar', 'error');
  };

  const excluirDefinitivamente = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Excluir definitivamente?', icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Excluir definitivamente', cancelButtonText: 'Voltar', confirmButtonColor: '#dc2626',
      html: `Isso remove por completo <strong>${detalhe.descricao}</strong>.<br/>Só é permitido quando não há pagamento confirmado — não pode ser desfeito.`,
    });
    if (!isConfirmed) return;
    const r = await apiFin(`${endpoint}/excluir`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Conta excluída definitivamente.', 'success'); onChanged(); onClose(); }
    else showToast(r.mensagem || 'Erro ao excluir', 'error');
  };

  const enviarCobranca = async () => {
    setEnviandoCobranca(true);
    const r = await apiFin(`/receivables/${item.id}/enviar-cobranca`, { method: 'POST', body: JSON.stringify({}) });
    setEnviandoCobranca(false);
    if (r.tentativa && (r.tentativa.status === 'ENVIADA' || r.tentativa.status === 'DRY_RUN')) {
      showToast(`Cobrança ${r.tentativa.status === 'DRY_RUN' ? 'simulada (Dry Run)' : 'enviada'}!`, 'success');
    } else showToast(`Falha ao enviar: ${r.tentativa?.erro || r.mensagem || 'erro desconhecido'}`, 'error');
    carregar();
  };

  const enviarDocumento = async () => {
    if (!docTitulo.trim() || !docArquivo) { showToast('Informe o título e selecione um arquivo', 'error'); return; }
    setSalvandoDoc(true);
    const formData = new FormData();
    formData.append('titulo', docTitulo);
    formData.append('tipo', 'OUTRO');
    formData.append('arquivo', docArquivo);
    formData.append('relacoes', JSON.stringify([{ entity_type: isReceita ? 'RECEIVABLE' : 'PAYABLE', entity_id: item.id }]));
    const r = await apiForm('/documentos', { method: 'POST', body: formData });
    setSalvandoDoc(false);
    if (r.status === 'ok') { showToast('Documento adicionado!', 'success'); setShowNovoDoc(false); setDocTitulo(''); setDocArquivo(null); carregarDocumentos(); }
    else showToast(r.mensagem || 'Erro ao enviar documento', 'error');
  };

  if (!detalhe) return <Sheet open onClose={onClose} title="Carregando..."><FontAwesomeIcon icon={faSpinner} spin /></Sheet>;

  const valorTotal = parseFloat(detalhe.valor);
  const valorPago = parseFloat(detalhe.valor_pago || 0);
  const saldo = parseFloat(detalhe.saldo ?? (valorTotal - valorPago));
  const podePagar = detalhe.status !== 'PAGO' && detalhe.status !== 'CANCELADO';
  const temPagamentoConfirmado = (detalhe.pagamentos || []).some((p) => p.status === 'CONFIRMADO');

  const itensMenu = [
    { label: 'Duplicar', onClick: duplicar },
    { label: 'Arquivar', onClick: arquivar },
    { label: 'Cancelar conta', tone: 'danger', onClick: cancelar, visivel: detalhe.status !== 'CANCELADO' && detalhe.status !== 'PAGO' },
    {
      label: 'Excluir definitivamente', tone: 'danger', onClick: excluirDefinitivamente,
      disabled: temPagamentoConfirmado,
      hint: temPagamentoConfirmado ? 'Há pagamento confirmado — use Arquivar' : undefined,
    },
  ];

  return (
    <Sheet open onClose={onClose} title={detalhe.descricao} subtitle={isReceita ? 'Conta a receber' : `Conta a pagar — ${item.tipo}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          <StatusBadge status={detalhe.situacao || detalhe.status} />
          {detalhe.status === 'CANCELADO' && (
            <Button size="sm" variant="primary" onClick={reativar}>Reativar (desfazer cancelamento)</Button>
          )}
        </div>

        {/* Hero: saldo é a informação de maior destaque, nunca o valor
            bruto original (§15/§18 da correção — bug crítico corrigido). */}
        {!editando ? (
          <div>
            {saldo > 0 ? (
              <>
                <div style={{ fontSize: 'var(--crm-text-3xl)', fontWeight: 700, letterSpacing: '-0.01em', color: detalhe.situacao?.includes('VENCIDO') ? 'var(--crm-danger)' : 'var(--crm-text-primary)' }}>
                  {fmtBRL(saldo)} <span className="crm-form-hint" style={{ fontSize: 'var(--crm-text-base)', fontWeight: 400 }}>em aberto</span>
                </div>
                {valorPago > 0 ? (
                  <div className="crm-form-hint">de {fmtBRL(valorTotal)} — {fmtBRL(valorPago)} já {isReceita ? 'recebido' : 'pago'}</div>
                ) : (
                  <div className="crm-form-hint">vencimento {fmtDate(detalhe.vencimento)}</div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 'var(--crm-text-3xl)', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--crm-positive)' }}>
                {fmtBRL(valorTotal)} <span className="crm-form-hint" style={{ fontSize: 'var(--crm-text-base)', fontWeight: 400 }}>{isReceita ? 'recebido' : 'pago'}</span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
            <div className="crm-form-field" style={{ flex: 1, minWidth: '160px' }}>
              <label className="crm-form-label">Valor{!podeEditarValor && (detalhe.status === 'PAGO' ? ' (bloqueado — conta já paga, estorne primeiro)' : ' (bloqueado — conta cancelada)')}</label>
              <input className="crm-input" type="number" step="0.01" disabled={!podeEditarValor} value={form.valor || ''} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
            </div>
            <div className="crm-form-field" style={{ flex: 1, minWidth: '160px' }}>
              <label className="crm-form-label">Vencimento</label>
              <input className="crm-input" type="date" value={form.vencimento || ''} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} />
            </div>
          </div>
        )}

        {/* Ações principais — pagar/editar/cobrar em no máximo 1 clique
            daqui (§16/§23). */}
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          {podePagar && <Button variant="primary" size="sm" onClick={() => setPagamentoAberto(true)}>Registrar {fmtBRL(saldo)}</Button>}
          <Button size="sm" variant="outline" onClick={() => setEditando((v) => !v)}>{editando ? 'Fechar edição' : 'Editar'}</Button>
          {isReceita && <Button size="sm" variant="outline" icon={faPaperPlane} loading={enviandoCobranca} onClick={enviarCobranca}>Cobrar</Button>}
          <RowMenu itens={itensMenu} />
        </div>

        {editando ? (
          <Panel title="Editar dados">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
              <div className="crm-form-field"><label className="crm-form-label">Descrição</label><input className="crm-input" value={form.descricao || ''} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
              {isReceita ? (
                <div className="crm-form-field">
                  <label className="crm-form-label">Cliente</label>
                  <select className="crm-select" value={form.cliente_id || ''} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}>
                    <option value="">Não vinculado</option>
                    {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
                  </select>
                </div>
              ) : (
                <div className="crm-form-field"><label className="crm-form-label">Fornecedor</label><input className="crm-input" value={form.fornecedor_nome || ''} onChange={(e) => setForm({ ...form, fornecedor_nome: e.target.value })} /></div>
              )}
              <div className="crm-form-field"><label className="crm-form-label">Categoria</label><input className="crm-input" value={form.categoria || ''} onChange={(e) => setForm({ ...form, categoria: e.target.value })} /></div>
              <div className="crm-form-field"><label className="crm-form-label">Competência</label><input className="crm-input" placeholder="AAAA-MM" value={form.competencia || ''} onChange={(e) => setForm({ ...form, competencia: e.target.value })} /></div>
              <div className="crm-form-field"><label className="crm-form-label">Forma de pagamento</label><input className="crm-input" value={form.forma_pagamento_prevista || ''} onChange={(e) => setForm({ ...form, forma_pagamento_prevista: e.target.value })} /></div>
              {isReceita && (
                <>
                  <div className="crm-form-field"><label className="crm-form-label">Contato de cobrança — nome</label><input className="crm-input" value={form.contato_nome || ''} onChange={(e) => setForm({ ...form, contato_nome: e.target.value })} /></div>
                  <div className="crm-form-field"><label className="crm-form-label">Telefone/WhatsApp</label><input className="crm-input" value={form.contato_telefone || ''} onChange={(e) => setForm({ ...form, contato_telefone: e.target.value })} /></div>
                  <div className="crm-form-field"><label className="crm-form-label">Notas</label><textarea className="crm-input" rows={3} value={form.notas || ''} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
                </>
              )}
              {!isReceita && (
                <div className="crm-form-field"><label className="crm-form-label">Observações</label><textarea className="crm-input" rows={3} value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
              )}
              <Button variant="primary" loading={salvando} onClick={salvarEdicao}>Salvar</Button>
            </div>
          </Panel>
        ) : (
          <Panel title="Detalhes">
            <div>
              {isReceita && (
                <LinhaDetalhe rotulo="Cliente">
                  {detalhe.cliente_id ? <EntityLink onClick={() => onNavegar?.('clientes', { clienteId: detalhe.cliente_id })}>{detalhe.cliente_nome || 'Cliente'}</EntityLink> : <UnlinkedField />}
                </LinhaDetalhe>
              )}
              <div ref={vincularProjetoBtnRef}>
                <LinhaDetalhe rotulo="Projeto">
                  {detalhe.projeto_id ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)' }}>
                      <EntityLink onClick={() => onNavegar?.('projetos', { projetoId: detalhe.projeto_id })}>{detalhe.projeto_nome || 'Projeto'}</EntityLink>
                      <button type="button" className="crm-entity-link" onClick={() => setShowVincularProjeto((v) => !v)}>Alterar</button>
                    </span>
                  ) : (
                    <UnlinkedField actionLabel="+ Vincular" onAction={() => setShowVincularProjeto((v) => !v)} />
                  )}
                  {showVincularProjeto && (
                    <VincularProjetoSelector
                      anchorRef={vincularProjetoBtnRef}
                      clienteId={detalhe.cliente_id}
                      projetoAtualId={detalhe.projeto_id}
                      onSelecionar={vincularProjeto}
                      onFechar={() => setShowVincularProjeto(false)}
                    />
                  )}
                </LinhaDetalhe>
              </div>
              {!isReceita && item.tipo === 'FOLHA' ? (
                <LinhaDetalhe rotulo="Pessoa">{detalhe.pessoa_folha_nome || <UnlinkedField label="Não informado" />}</LinhaDetalhe>
              ) : !isReceita && (
                <LinhaDetalhe rotulo="Fornecedor">{detalhe.fornecedor_nome || <UnlinkedField label="Não informado" />}</LinhaDetalhe>
              )}
              {detalhe.recurrence_rule_id && (
                <LinhaDetalhe rotulo="Assinatura"><EntityLink onClick={() => onAbrirAssinatura(detalhe.recurrence_rule_id)}>Ver assinatura</EntityLink></LinhaDetalhe>
              )}
              <LinhaDetalhe rotulo="Vencimento">{fmtDate(detalhe.vencimento)}</LinhaDetalhe>
              <LinhaDetalhe rotulo="Competência" ><span style={{ textTransform: 'capitalize' }}>{nomeCompetencia(detalhe.competencia)}</span></LinhaDetalhe>
              <LinhaDetalhe rotulo="Categoria">{detalhe.categoria || '—'}</LinhaDetalhe>
              <LinhaDetalhe rotulo="Forma">{detalhe.forma_pagamento_prevista || '—'}</LinhaDetalhe>
              {isReceita && detalhe.contato_cobranca?.nome && (
                <LinhaDetalhe rotulo="Contato">{detalhe.contato_cobranca.nome} — {detalhe.contato_cobranca.telefone || detalhe.contato_cobranca.email || '—'}</LinhaDetalhe>
              )}
              {(isReceita ? detalhe.notas : detalhe.observacoes) && (
                <LinhaDetalhe rotulo={isReceita ? 'Notas' : 'Observações'}>{isReceita ? detalhe.notas : detalhe.observacoes}</LinhaDetalhe>
              )}
            </div>
          </Panel>
        )}

        {detalhe.pagamentos?.length > 0 && (
          <Panel title="Histórico de pagamentos" flush>
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Data</th><th>Forma</th><th>Valor</th><th /></tr></thead>
                <tbody>
                  {detalhe.pagamentos.map((p) => (
                    <tr key={p.id} style={p.status === 'CANCELADO' ? { opacity: 0.5, textDecoration: 'line-through' } : undefined}>
                      <td>{fmtDate(p.data_pagamento)}</td>
                      <td>{p.forma_pagamento || '—'}</td>
                      <td>{fmtBRL(p.valor)}</td>
                      <td>{p.status === 'CONFIRMADO' && <Button size="sm" variant="ghost" onClick={() => estornar(p.id)}>Estornar</Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {isReceita && (detalhe.billing_automation || detalhe.billing_attempts?.length > 0) && (
          <Panel title="Cobrança automática" actions={<Button size="sm" variant="ghost" onClick={() => setShowCobranca(true)}>Configurar régua</Button>}>
            <Badge tone={detalhe.billing_automation?.ativa ? 'positive' : 'neutral'}>
              {detalhe.billing_automation ? (detalhe.billing_automation.ativa ? 'Automação ativa' : 'Automação pausada/não configurada') : 'Sem automação configurada'}
            </Badge>
            {detalhe.billing_attempts?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)', marginTop: 'var(--crm-space-2)' }}>
                {detalhe.billing_attempts.slice(0, 5).map((a) => (
                  <div key={a.id} style={{ fontSize: 'var(--crm-text-xs)' }}>
                    <Badge tone={a.status === 'ENVIADA' ? 'positive' : a.status === 'DRY_RUN' ? 'warning' : 'danger'}>{a.gatilho_evento}</Badge>
                    <div className="crm-form-hint">{a.mensagem_texto?.slice(0, 80)}...</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        )}
        {isReceita && !detalhe.billing_automation && !detalhe.billing_attempts?.length && (
          <Button size="sm" variant="ghost" onClick={() => setShowCobranca(true)} style={{ alignSelf: 'flex-start' }}>+ Configurar cobrança automática</Button>
        )}

        <Panel title="Documentos" actions={<Button size="sm" variant="ghost" onClick={() => setShowNovoDoc(true)}>+ Documento</Button>} flush>
          {documentos === null ? (
            <div style={{ padding: 'var(--crm-space-4)' }} className="crm-form-hint">Carregando...</div>
          ) : documentos.length === 0 ? (
            <EmptyState title="Nenhum documento anexado" />
          ) : (
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Título</th><th>Tipo</th></tr></thead>
                <tbody>{documentos.map((d) => <tr key={d.id}><td className="crm-table-primary">{d.titulo}</td><td><Badge tone="accent">{d.tipo}</Badge></td></tr>)}</tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {pagamentoAberto && (
        <RegistrarPagamentoDialog item={{ ...detalhe, id: item.id, _origem: item._origem }} onClose={() => setPagamentoAberto(false)} onChanged={notificarMudanca} showToast={showToast} />
      )}

      {showNovoDoc && (
        <Sheet open onClose={() => setShowNovoDoc(false)} title="Novo Documento" footer={<Button variant="primary" loading={salvandoDoc} onClick={enviarDocumento}>Salvar</Button>}>
          <div className="crm-form-field"><label className="crm-form-label">Título</label><input className="crm-input" value={docTitulo} onChange={(e) => setDocTitulo(e.target.value)} /></div>
          <div className="crm-form-field"><label className="crm-form-label">Arquivo</label><input type="file" onChange={(e) => setDocArquivo(e.target.files[0])} /></div>
        </Sheet>
      )}

      {showCobranca && (
        <CobrancaWizardSheet
          receivableIdInicial={item.id}
          onClose={() => setShowCobranca(false)}
          onSalvo={() => { setShowCobranca(false); notificarMudanca(); }}
          showToast={showToast}
        />
      )}
    </Sheet>
  );
}

export default LancamentoSheet;
